import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import find from 'lodash-es/find';
import {combineLatest, forkJoin, Observable, of, Subject} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {TrackerCandidateStatus} from '../../../model';
import {trackerCandidateStatusForId} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {IdentifyModalComponent} from '../../shared/identify-modal/identify-modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';

@Component({
  selector: 'candidate-profile-page',
  templateUrl: './candidate-profile-page.component.html',
  styleUrls: ['./candidate-profile-page.component.scss']
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class CandidateProfilePageComponent implements OnInit {

  positionId: string;
  returnTo: string;
  candidateReturnTo = '';
  workingProfileReturnTo = '';
  rootReturnTo = '';
  view: string; // Talent Pools view

  order: Order;
  submittal: Submittal;
  staff: StaffInfo[];
  admin$: Observable<boolean>;
  authenticated$: Observable<boolean>;
  reload$ = new Subject<void>();

  favorite = false;
  saving = false;
  saveError: string | null = null;
  brand$ = this.AppState.brand$;
  masquerading$ = this.Auth.masquerading$;

  @ViewChild('identifyModal') identifyModal: IdentifyModalComponent;
  identifyCallback: Function | null = null;

  constructor(private Router: Router,
              private route: ActivatedRoute,
              private Navigation: NavigationService,
              private AppState: AppStateService,
              private Api: ClientApiService,
              private Auth: AuthService,
              private Analytics: AnalyticsService,
              private Translate: TranslateService) {
  }

  ngOnInit() {
    window.scrollTo(0, 0); // scroll to top hack

    combineLatest(this.route.paramMap, this.route.queryParamMap, this.reload$)
      .pipe(
        tap(() => this.AppState.loading$.next(true)),
        switchMap(([params, query]) => {
          this.initializeReturnTo(query);
          this.positionId = params.get('id');
          this.view = query.get('view');
          return forkJoin(
            this.Api.getOrder(params.get('id')),
            this.Api.getSubmittals(params.get('id')),
            this.Api.getFavorites(),
            of(params.get('candidate'))
          );
        }),
        switchMap(([order, submittals, favorites, profileId]) => {
          this.order = order;
          this.staff = submittals.staff;
          this.submittal = find(submittals.submittals, {id: profileId});
          this.favorite = favorites && favorites.includes(this.submittal.personId);
          return this.updateTrackerStatus();
        })
      )
      .subscribe(() => this.AppState.loading$.next(false),
        (err: HttpError) => {
          console.error('Failed to load candidate profile information.', err);
          this.Navigation.handleError(err);
        });
    this.reload$.next();
    this.authenticated$ = this.Auth.user$.pipe(map(v => !!v));
    this.admin$ = this.Auth.user$.pipe(map(v => v && v.admin));
  }

  initializeReturnTo(query: any) {
    this.returnTo = query.get('returnTo');

    if (this.returnTo) {
      this.rootReturnTo = this.returnTo.split(',')[0];
      this.candidateReturnTo = this.returnTo.split(',candidates')[0].replace('working-positions', 'positions');
      this.workingProfileReturnTo = this.returnTo.split(',working-profile')[0].replace('working-positions', 'positions');
    }
  }

  savePositionTitle(position: string) {
    this.Api.saveTitle(this.submittal.orderId, position)
      .subscribe(() => this.order = {...this.order, position},
        err => this.saveError = `Failed to save position title. ${err.code}`);
  }

  getTrackerStatus(): string {
    if (this.submittal) {
      return TrackerCandidateStatus[trackerCandidateStatusForId(this.submittal.statusId)];
    }
    return '';
  }

  toggleFavorite(): Promise<void> {
    if (this.masquerading$.getValue()) {
      return;
    }
    if (!this.Auth.isFullyAuthenticated()) {
      this.identifyCallback = () => {
        if (this.Auth.isFullyAuthenticated()) {
          this.Api.getFavorites()
            .subscribe(favorites => {
              if (favorites && this.submittal.personId) {
                this.favorite = favorites.includes(this.submittal.personId);
                if (!this.favorite) {
                  this.toggleFavorite()
                    .then(() => this.reload$.next());
                }
              }
            }, (err: HttpError) =>
              this.Navigation.handleError(err));
        }
      };
      this.identifyModal.show();
      return;
    }

    if (this.saving) {
      return;
    }
    this.favorite = !this.favorite;
    this.Analytics.trackEvent('Candidate favorite/unfavorite clicked', {
      category: 'Action'
    });
    this.saving = true;
    return (this.favorite ? this.Api.addFavorite : this.Api.removeFavorite)
      .call(this.Api, this.submittal.personId)
      .toPromise()
      .then(() => {
        this.Analytics.trackEvent('Candidate favorite/unfavorite succeeded');
        this.saving = false;
      }, (err: HttpError) => {
        console.error('Failed to update favorites.', err);
        this.Analytics.trackEvent('Candidate favorite/unfavorite failed', {category: 'Error'});
        this.saveError = this.Translate.instant('talent-pool.favorite-save-error', {error: err && err.code});
        this.favorite = !this.favorite;
        this.saving = false;
      });
  }

  profileUpdated(profile: Talent.Profile) {
    this.submittalUpdated({...this.submittal, profile});
  }

  submittalUpdated(submittal: Submittal) {
    // Eventually this should reload the profile from the server
    this.submittal = submittal;
  }

  ctaComplete() {
    this.reload$.next();
  }

  showIdentification() {
    this.identifyCallback = () => this.reload$.next();
    this.identifyModal.show();
  }

  private updateTrackerStatus(): Observable<any> {
    if (this.masquerading$.getValue()) {
      return of(null);
    }
    if (this.Auth.isFullyAuthenticated()
      && !this.Auth.user$.getValue().admin
      && trackerCandidateStatusForId(this.submittal.statusId) === TrackerCandidateStatus.new
      && this.route.snapshot.queryParamMap.has('review')) {
      this.Analytics.trackEvent('Order CTA Submitted: Review', {category: 'Action'});
      return this.Api.reviewCandidate(this.submittal.id).pipe(map(() => {
        this.Analytics.trackEvent('Order CTA Succeeded: Review', {category: 'Action'});
        // FIXME api call should return new submittal info
        this.submittal = {...this.submittal, statusId: 340 /* Reviewed by Client */};
      }), catchError(err => {
        this.Analytics.trackEvent('Order CTA Failed: Review', {category: 'Action'});
        throw err;
      }));
    }
    return of(null);
  }
}
